import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { SelectMultiple } from 'tea-component';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';

export default function MultiSelectFilter(props: {
  options: Array<{ text: string; value: string }>;
  /** 受控模式：外部 value 变化会同步到内部渲染 */
  value?: string[];
  /** 非受控模式：仅用于初始化 */
  defaultValue?: string[];
  onChange?: (value: string[]) => void;
  className?: string;
  placeholder?: string;
  /** 下拉关闭时触发，返回最终选中值 */
  onClose?: (value: string[]) => void;
  matchButtonWidth?: boolean;
  /** 空数组表示全选 */
  emptyAsAll?: boolean;
  listHeight?: number;
}) {
  const {
    listHeight = 300,
    options,
    value: controlledValue,
    defaultValue,
    onChange,
    className,
    placeholder,
    onClose,
    matchButtonWidth = true,
    emptyAsAll = false,
  } = props;
  const { formatMessage, formatOptionsMessage } = useLanguage();

  const isControlled = controlledValue !== undefined;
  const allValues = useMemo(() => options.map((o) => o.value), [options]);

  const toExternalValue = useCallback(
    (v: string[]) => {
      if (emptyAsAll && v.length === allValues.length) return [];
      return v;
    },
    [emptyAsAll, allValues],
  );

  // 用 ref 持有最新转换函数，避免 useEffect 因函数引用变化反复触发
  const toInternalRef = useRef((v: string[]) => v);
  toInternalRef.current = useCallback(
    (v: string[]) => (emptyAsAll && v.length === 0 ? [...allValues] : v),
    [emptyAsAll, allValues],
  );

  const [internalValue, setInternalValue] = useState(() => {
    const v = controlledValue ?? defaultValue ?? [];
    return emptyAsAll && v.length === 0 ? [...allValues] : v;
  });

  // 受控模式：外部 value 变化同步到内部
  useEffect(() => {
    if (isControlled) {
      setInternalValue(toInternalRef.current(controlledValue));
    }
  }, [isControlled, controlledValue, emptyAsAll, allValues]);

  const handleChange = useCallback(
    (val: string[]) => {
      setInternalValue(val);
      onChange?.(toExternalValue(val));
    },
    [onChange, toExternalValue],
  );

  const handleClose = useCallback(() => {
    onClose?.(toExternalValue(internalValue));
  }, [onClose, internalValue, toExternalValue]);

  const selectedCount = internalValue.length || 0;
  const isAllSelected = internalValue.length === allValues.length;

  return (
    <SelectMultiple
      className={`dbo-multi-select ${className ? className : ''}`}
      appearance="button"
      options={formatOptionsMessage(options)}
      value={internalValue}
      matchButtonWidth={matchButtonWidth}
      staging={false}
      listHeight={listHeight}
      allOption={{
        value: 'all',
        text: formatMessage({ id: 'All' }),
      }}
      onChange={handleChange}
      onClose={handleClose}
      button={() => {
        let displayText;
        if (isAllSelected) {
          displayText = formatMessage({ id: 'All' });
        } else if (selectedCount === 0) {
          displayText = formatMessage({ id: 'None' });
        } else if (selectedCount <= 2) {
          const selectedTexts = internalValue.map((val) => {
            const opt = options.find((o) => o.value === val);
            return opt?.text || val;
          });
          displayText = selectedTexts.join(', ');
        } else {
          displayText = `${selectedCount}`;
        }

        return (
          <div className="dbo-select-button">
            <span className="dbo-select-button-text">
              {placeholder && (
                <span className="dbo-select-button-placeholder">
                  {placeholder}{' '}
                </span>
              )}
              <span className="dbo-select-button-value">{displayText}</span>
            </span>
          </div>
        );
      }}
    />
  );
}
